import { z } from 'zod';
import { categoryIdSchema, investmentIdSchema, itemIdSchema } from './ids';
import { anchorsSchema } from './key-date';
import { MAX_HORIZON_MONTHS } from './limits';
import { amountSchema, fractionSchema } from './money';
import { rateOrInheritSchema, rateSpecSchema } from './rate';
import { scheduledSchema } from './scheduled';
import { monthIndexSchema } from './time';

/*
 * Every user-facing item maps to one engine primitive (CLAUDE.md "Domain model"):
 *   flow    → scheduled flow   (salary, rent, subscriptions, one-off events, transfers to accounts)
 *   formula → derived flow     (tax, "save 20 % of income")
 *   asset   → account (asset)  (savings, deposits, investments, property)
 *   loan    → account (liability)
 * `template` only drives UI icon/defaults; the engine never branches on it.
 */

export const DIRECTIONS = ['in', 'out'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const FREQUENCIES = ['monthly', 'quarterly', 'semiannual', 'yearly', 'once'] as const;
export type Frequency = (typeof FREQUENCIES)[number];

/** Months between occurrences; 0 = a single occurrence at `start`. */
export const FREQUENCY_MONTHS: Record<Frequency, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  yearly: 12,
  once: 0,
};

/**
 * - `today`: amounts are in project-start money and are indexed from month 0 (an inheritance of
 *   "€20 000 in today's money" in 2035 grows with its index until 2035).
 * - `atStart`: amounts are nominal at the start of the item (or of their schedule period);
 *   indexation starts from there.
 */
export const AMOUNT_BASES = ['today', 'atStart'] as const;
export type AmountBasis = (typeof AMOUNT_BASES)[number];

/** `annual` holds the indexed amount for 12 months at a time (e.g. a salary raised once a year). */
export const GROWTH_TIMINGS = ['monthly', 'annual'] as const;
export type GrowthTiming = (typeof GROWTH_TIMINGS)[number];

export const growthSchema = z.discriminatedUnion('kind', [
  /** Stays nominally fixed (and loses real value). */
  z.object({ kind: z.literal('none') }),
  /**
   * Follows inflation: item override → category default → global CPI, resolved per month.
   * Override segments of `inherit` fall through to the next level for that period.
   */
  z.object({
    kind: z.literal('indexed'),
    override: scheduledSchema(rateOrInheritSchema).optional(),
    timing: z.enum(GROWTH_TIMINGS),
  }),
  /** An explicit growth rate (fixed, CPI + x, or a distribution). */
  z.object({
    kind: z.literal('rate'),
    rate: scheduledSchema(rateSpecSchema),
    timing: z.enum(GROWTH_TIMINGS),
  }),
  /** Step raises: +`raise` every `everyMonths` from the item start (e.g. +5 % every 2 years). */
  z.object({
    kind: z.literal('steps'),
    raise: z.number().min(-0.99).max(10),
    everyMonths: z.number().int().min(1).max(MAX_HORIZON_MONTHS),
  }),
]);
export type Growth = z.infer<typeof growthSchema>;

const itemBase = {
  id: itemIdSchema,
  name: z.string().trim().min(1).max(120),
  template: z.string().min(1).max(64),
  notes: z.string().max(4000).optional(),
  /** The investment this item belongs to, for investments scoped to `items`. */
  investment: investmentIdSchema.optional(),
};

const window = {
  start: monthIndexSchema,
  /** Last month (inclusive) in which the item is active. Omit to run to the horizon. */
  end: monthIndexSchema.optional(),
  /** `start` and `end` tied to key dates. */
  anchors: anchorsSchema('start', 'end'),
};

const endNotBeforeStart = (v: { start: number; end?: number | undefined }): boolean =>
  v.end === undefined || v.end >= v.start;
const endIssue = { message: 'End must not be before start', path: ['end'] };

export const flowSchema = z
  .object({
    ...itemBase,
    type: z.literal('flow'),
    direction: z.enum(DIRECTIONS),
    amount: scheduledSchema(amountSchema),
    amountBasis: z.enum(AMOUNT_BASES),
    frequency: z.enum(FREQUENCIES),
    ...window,
    growth: growthSchema,
    category: categoryIdSchema.optional(),
    /**
     * When set, this flow is a transfer between cash and an asset account:
     * `out` = contribution (cash → account), `in` = withdrawal (account → cash, capped at its balance).
     */
    account: itemIdSchema.optional(),
  })
  .refine(endNotBeforeStart, endIssue);
export type Flow = z.infer<typeof flowSchema>;

export const formulaSourceSchema = z.object({
  ref: z.discriminatedUnion('type', [
    z.object({ type: z.literal('item'), id: itemIdSchema }),
    z.object({ type: z.literal('category'), id: categoryIdSchema }),
    /** All scheduled (non-formula, non-transfer) inflows of the scenario. */
    z.object({ type: z.literal('totalIncome') }),
    /** All scheduled (non-formula, non-transfer) outflows of the scenario. */
    z.object({ type: z.literal('totalExpenses') }),
    /** The monthly return (interest/growth) earned by an asset account, e.g. to tax interest. */
    z.object({ type: z.literal('accountReturn'), id: itemIdSchema }),
  ]),
  /** The source's monthly magnitude is added (+1) or subtracted (−1). */
  sign: z.union([z.literal(1), z.literal(-1)]),
});
export type FormulaSource = z.infer<typeof formulaSourceSchema>;
export type FormulaRef = FormulaSource['ref'];

export const percentOfSpecSchema = z
  .object({
    kind: z.literal('percentOf'),
    // May be empty while being edited (the result is then 0).
    sources: z.array(formulaSourceSchema).max(64),
    percent: scheduledSchema(fractionSchema),
    /** Monthly bounds on the result, in nominal money. */
    floor: amountSchema.optional(),
    cap: amountSchema.optional(),
  })
  .refine((s) => s.floor === undefined || s.cap === undefined || s.floor <= s.cap, {
    message: 'Floor must not exceed cap',
    path: ['cap'],
  });

export const taxBracketSchema = z.object({
  /** Annual taxable amount from which `rate` applies (marginal). */
  from: amountSchema,
  rate: z.number().min(0).max(1),
});
export type TaxBracket = z.infer<typeof taxBracketSchema>;

export const taxBracketsSchema = z
  .array(taxBracketSchema)
  .min(1)
  .max(20)
  .superRefine((brackets, ctx) => {
    if (brackets[0]?.from !== 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'The first bracket must start at 0',
        path: [0, 'from'],
      });
    }
    for (let i = 1; i < brackets.length; i++) {
      const prev = brackets[i - 1];
      const cur = brackets[i];
      if (prev && cur && cur.from <= prev.from) {
        ctx.addIssue({
          code: 'custom',
          message: 'Brackets must be in ascending order',
          path: [i, 'from'],
        });
      }
    }
  });

/**
 * A deduction from the taxable base that is usually also paid (e.g. employee social
 * contributions): `rate × clamp(base, floor, cap)` per month.
 */
export const taxDeductionSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80),
  rate: scheduledSchema(z.number().min(0).max(1)),
  /** Monthly minimum/maximum base (e.g. minimum and maximum insurable income). */
  floor: scheduledSchema(amountSchema).optional(),
  cap: scheduledSchema(amountSchema).optional(),
  /** Also book the deduction as an outflow of this item (contributions are paid). */
  emitAsFlow: z.boolean(),
});
export type TaxDeduction = z.infer<typeof taxDeductionSchema>;

export const TAX_SETTLEMENTS = ['monthly', 'annualCumulative'] as const;
export type TaxSettlement = (typeof TAX_SETTLEMENTS)[number];

/**
 * A tax computed from other items (docs/PLAN.md §3.2). Country rules are data (presets), never
 * engine branches. Per month: base = Σ sign × source; deductions = rate × clamp(base, floor, cap);
 * taxable = base − deductions; tax = brackets(taxable − allowance) − credits.
 */
export const taxSpecSchema = z.object({
  kind: z.literal('tax'),
  sources: z.array(formulaSourceSchema).max(64),
  deductions: z.array(taxDeductionSchema).max(10),
  /** Annual thresholds; a single bracket is a flat tax. */
  brackets: scheduledSchema(taxBracketsSchema),
  /** Scale thresholds, caps, floors and allowances with cumulative CPI (per calendar year). */
  indexThresholds: z.enum(['none', 'cpi']),
  /** Annual tax-free amount. */
  allowance: scheduledSchema(amountSchema).optional(),
  /** Annual fixed tax credits. */
  credits: scheduledSchema(amountSchema).optional(),
  /**
   * `monthly`: each month taxed on its own (annual amounts ÷ 12).
   * `annualCumulative`: withhold the year-to-date liability so far (correct for progressive
   * brackets and irregular income); the tax year is the calendar year.
   */
  settlement: z.enum(TAX_SETTLEMENTS),
  /** Provenance only (which preset the values were copied from); the engine ignores it. */
  presetId: z.string().max(60).optional(),
});
export type TaxSpec = z.infer<typeof taxSpecSchema>;

/** Formula kinds; both share `sources`. */
export const formulaSpecSchema = z.discriminatedUnion('kind', [percentOfSpecSchema, taxSpecSchema]);
export type FormulaSpec = z.infer<typeof formulaSpecSchema>;
export type PercentOfSpec = z.infer<typeof percentOfSpecSchema>;

export const formulaSchema = z
  .object({
    ...itemBase,
    type: z.literal('formula'),
    direction: z.enum(DIRECTIONS),
    spec: formulaSpecSchema,
    ...window,
    category: categoryIdSchema.optional(),
    account: itemIdSchema.optional(),
  })
  .refine(endNotBeforeStart, endIssue);
export type Formula = z.infer<typeof formulaSchema>;

export const MAX_ASSET_PAYMENTS = 24;

/** One instalment of an asset's price: a share of `openingBalance`, paid from cash in `month`. */
export const assetPaymentSchema = z.object({
  month: monthIndexSchema,
  share: z.number().gt(0).max(1),
  anchors: anchorsSchema('month'),
});
export type AssetPayment = z.infer<typeof assetPaymentSchema>;

export const assetSchema = z
  .object({
    ...itemBase,
    type: z.literal('asset'),
    /** Balance when the account opens at `start`. */
    openingBalance: amountSchema,
    start: monthIndexSchema,
    /** Opening balance is paid out of cash (e.g. buying a property) rather than already owned. */
    fundedFromCash: z.boolean(),
    /**
     * When funded from cash: the price paid in instalments (e.g. a flat bought off-plan, 20 % at
     * signing and 80 % at completion). Until paid, the rest is owed to the seller, interest-free.
     * Omit to pay it all at `start`.
     */
    payments: z.array(assetPaymentSchema).min(1).max(MAX_ASSET_PAYMENTS).optional(),
    /** Month in which the whole balance is sold/withdrawn to cash and the account closes. */
    liquidateAt: monthIndexSchema.optional(),
    /** Annual return (effective), e.g. 5 % for an ETF, CPI + 1 % for property. */
    return: scheduledSchema(rateSpecSchema),
    anchors: anchorsSchema('start', 'liquidateAt'),
  })
  .superRefine((a, ctx) => {
    if (a.liquidateAt !== undefined && a.liquidateAt <= a.start) {
      ctx.addIssue({
        code: 'custom',
        message: 'Liquidation must be after the account opens',
        path: ['liquidateAt'],
      });
    }
    if (a.payments === undefined) return;
    if (!a.fundedFromCash) {
      ctx.addIssue({
        code: 'custom',
        message: 'Instalments need the price to be paid from cash',
        path: ['payments'],
      });
    }
    a.payments.forEach((p, i) => {
      const previous = i === 0 ? a.start - 1 : (a.payments?.[i - 1]?.month ?? a.start);
      if (p.month <= previous) {
        ctx.addIssue({
          code: 'custom',
          message: 'Instalments must be in order, from the month the account opens',
          path: ['payments', i, 'month'],
        });
      }
    });
    const total = a.payments.reduce((sum, p) => sum + p.share, 0);
    if (Math.abs(total - 1) > 1e-9) {
      ctx.addIssue({
        code: 'custom',
        message: 'Instalments must add up to 100 %',
        path: ['payments'],
      });
    }
  });
export type Asset = z.infer<typeof assetSchema>;

export const AMORTIZATIONS = ['annuity', 'linear'] as const;
export type Amortization = (typeof AMORTIZATIONS)[number];

export const REPAYMENT_MODES = ['reduceTerm', 'reducePayment'] as const;
export type RepaymentMode = (typeof REPAYMENT_MODES)[number];

export const earlyRepaymentSchema = z.object({
  month: monthIndexSchema,
  amount: amountSchema.positive(),
  mode: z.enum(REPAYMENT_MODES),
  anchors: anchorsSchema('month'),
});
export type EarlyRepayment = z.infer<typeof earlyRepaymentSchema>;

export const loanSchema = z
  .object({
    ...itemBase,
    type: z.literal('loan'),
    /** Outstanding balance at `start`. A loan at month 0 models existing debt. */
    principal: amountSchema.positive(),
    start: monthIndexSchema,
    /** Number of monthly payments; the first is due the month after `start`. */
    termMonths: z.number().int().min(1).max(MAX_HORIZON_MONTHS),
    /** Nominal annual rate; the monthly rate is rate / 12 (lending convention). */
    rate: scheduledSchema(rateSpecSchema),
    amortization: z.enum(AMORTIZATIONS),
    /** The principal is paid into cash at `start` (a new loan) rather than already spent. */
    disburseToCash: z.boolean(),
    earlyRepayments: z.array(earlyRepaymentSchema).max(240),
    upfrontFee: amountSchema.optional(),
    monthlyFee: amountSchema.optional(),
    /** Category for interest and fees in cash-flow charts. */
    category: categoryIdSchema.optional(),
    anchors: anchorsSchema('start'),
  })
  .superRefine((loan, ctx) => {
    loan.earlyRepayments.forEach((r, i) => {
      if (r.month <= loan.start || r.month > loan.start + loan.termMonths) {
        ctx.addIssue({
          code: 'custom',
          message: 'Early repayments must fall within the loan term',
          path: ['earlyRepayments', i, 'month'],
        });
      }
    });
  });
export type Loan = z.infer<typeof loanSchema>;

export const itemSchema = z.discriminatedUnion('type', [
  flowSchema,
  formulaSchema,
  assetSchema,
  loanSchema,
]);
export type Item = z.infer<typeof itemSchema>;
export type ItemType = Item['type'];

export type Primitive = 'flow' | 'formula' | 'account';

export function primitiveOf(item: Pick<Item, 'type'>): Primitive {
  return item.type === 'asset' || item.type === 'loan' ? 'account' : item.type;
}

/** Items that produce a monthly cash amount other formulas can reference. */
export function isFlowLike(item: Item): item is Flow | Formula {
  return item.type === 'flow' || item.type === 'formula';
}
